<script setup lang="ts">
import { cva } from "class-variance-authority";
import { cloneVNode, useSlots, type VNode } from "vue";
import type { ClassProps } from "../../lib/base-props";
import { cn } from "../../lib/utils";
import { normalizeVNode } from "../../lib/vnode";

interface Props extends ClassProps {
	direction?: "vertical" | "horizontal";
}

defineProps<Props>();

const joinCls = cva("join", {
	variants: {
		direction: {
			vertical: "join-vertical",
			horizontal: "join-horizontal",
		},
	},
	defaultVariants: {
		direction: "horizontal",
	},
});

const slots = useSlots();

type MapFn = (_: VNode) => VNode;
function mapNodes(nodes: VNode[], fn: MapFn): VNode[] {
	return normalizeVNode(nodes).map(fn);
}

function nodes(): VNode[] {
	const nodes = slots.default?.();
	if (nodes == null) {
		return [];
	}

	return mapNodes(nodes, (n) => {
		const mergedClasses = cn("join-item", n.props?.class);
		if (n.props?.class != null) {
			delete n.props.class;
		}

		return cloneVNode(n, { class: mergedClasses });
	});
}
</script>

<template>
	<div :class="cn(joinCls($props))">
		<component v-for="(node, idx) of nodes()" :is="node" :key="idx" />
	</div>
</template>
